import React from 'react';

import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { KnzComponentProps } from '../../types';

/**
 * テーブル（WorkflowTable ベース）。
 * params:
 *  - headers（ヘッダー行）: 次の2形式に対応する
 *      1) パイプ形式: `- 名前|説明`（1 行に `|` 区切りで列を並べる）
 *      2) リスト形式: `- 名前` `- 説明`（1 列 1 項目で並べる）
 *  - text（データ行）: 各行を `- A|説明A` のように `|` 区切りで書く
 *
 * 例（パイプ形式ヘッダー）:
 *   headers:
 *    - 名前|説明
 *   text:
 *    - A|説明A
 *    - B|説明B
 *
 * 例（リスト形式ヘッダー）:
 *   headers:
 *    - 名前
 *    - 説明
 *   text:
 *    - A|説明A
 *    - B|説明B
 */
const splitCells = (line: string): string[] => line.split('|').map((c) => c.trim());

/**
 * ヘッダーを解決する。
 * - リスト形式（`|` を含まない項目が並ぶ / 2 項目以上）: 各項目を 1 列として扱う。
 * - パイプ形式（単一行に `|` 区切り）: `|` で分割して列にする。
 * どちらの形式でも渡せるよう、内容から判定する（後方互換）。
 */
const resolveHeaders = (raw: string | string[] | undefined): string[] => {
  if (raw == null) return [];
  const items = Array.isArray(raw) ? raw : [raw];
  const nonEmpty = items.map((s) => s.trim()).filter((s) => s !== '');
  if (nonEmpty.length === 0) return [];
  // 複数項目があり、いずれも `|` を含まない → リスト形式（各項目 1 列）
  const hasPipe = nonEmpty.some((s) => s.includes('|'));
  if (nonEmpty.length > 1 && !hasPipe) {
    return nonEmpty.map((c) => c.trim());
  }
  // それ以外は先頭行をパイプ区切りとして解釈（従来仕様）
  return splitCells(nonEmpty[0]);
};

export const Table: React.FC<KnzComponentProps> = ({ node, renderInline }) => {
  const headers = resolveHeaders(node.params.headers as string | string[] | undefined);

  const rowsRaw = node.params.text;
  const rowLines: string[] = Array.isArray(rowsRaw) ? rowsRaw : rowsRaw ? [String(rowsRaw)] : [];
  const rows = rowLines.map(splitCells);

  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <MuiTable size="small">
        {headers.length > 0 && (
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              {headers.map((h, i) => (
                <TableCell key={i}>
                  <strong>{renderInline(h)}</strong>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {rows.map((row, r) => (
            <TableRow key={r}>
              {row.map((cell, c) => (
                <TableCell key={c}>{renderInline(cell)}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};

export default Table;
